<script setup>
import { ref } from 'vue';
import AppSidebar from './Sidebar.vue';
import AccordionItem from './AccordionItem.vue';
import RelationSection from './RelationSection.vue';
import EntityModal from './EntityModal.vue';
import { useStore } from '../store.js';

const props = defineProps({
  schema: Object,
  movie: Object,
  isCreatingNew: Boolean
});

const { updateMovie } = useStore();

const entityModalState = ref({
  open: false,
  mode: '',
  type: '',
  entity: null,
  parent: null,
  parentMovie: null,
  relationName: '',
  onSubmit: null
});


function getSectionId(sectionName) {
  return `section-${sectionName.replace(/[^a-zA-Z0-9]/g, '-')}`.toLowerCase();
}

function openEntityModal(mode, type, entity, parent, parentMovie, relationName, onSubmit) {
  entityModalState.value = {
    open: true,
    mode,
    type,
    entity: entity || {},
    parent: typeof parent !== 'undefined' ? parent : null,
    parentMovie: typeof parentMovie !== 'undefined' ? parentMovie : null,
    relationName: relationName || type,
    onSubmit: onSubmit || null
  };
}
function closeEntityModal() {
  entityModalState.value.open = false;
}

async function handleMovieUpdate() {
  if (props.movie && props.movie.id) {
    await updateMovie(props.movie.id, props.movie);
  }
}

async function handleEntityModalSubmit(data) {
  // Handle selecting an existing entity (add by ID)
  if (data && data.mode === 'select' && data.selectedId && entityModalState.value.type !== 'movie') {
    const type = entityModalState.value.type;
    const parentMovie = entityModalState.value.parentMovie;
    const relationName = entityModalState.value.relationName || type;
    // Ensure the relation array exists
    if (!Array.isArray(entityModalState.value.parent[relationName])) {
      entityModalState.value.parent[relationName] = [];
    }
    // Only add if not already present
    if (!entityModalState.value.parent[relationName].includes(data.selectedId)) {
      entityModalState.value.parent[relationName].push(data.selectedId);
      await updateMovie(parentMovie.id, parentMovie);
    }
    closeEntityModal();
    return;
  }
  // If editing or adding a nested entity, update it in the parent movie and call updateMovie
  if (entityModalState.value.type !== 'movie' && (entityModalState.value.mode === 'edit' || entityModalState.value.mode === 'add')) {
    const type = entityModalState.value.type;
    const parentMovie = entityModalState.value.parentMovie;
    const arr = parentMovie[type];
    if (Array.isArray(arr)) {
      if (entityModalState.value.mode === 'edit') {
        // Reference check before editing
        const usages = [];
        const id = data.id;
        const checkRefs = (obj, path = []) => {
          if (Array.isArray(obj)) {
            obj.forEach((item, idx) => checkRefs(item, path.concat(idx)));
          } else if (obj && typeof obj === 'object') {
            Object.entries(obj).forEach(([key, value]) => {
              if (Array.isArray(value) && value.includes(id)) {
                usages.push(`${path.concat(key).join('.')} (array)`);
              } else if (value === id) {
                usages.push(`${path.concat(key).join('.')}`);
              } else {
                checkRefs(value, path.concat(key));
              }
            });
          }
        };
        checkRefs(parentMovie);
        if (usages.length) {
          const msg = `This ${type} is used in:\n${usages.join('\n')}\n\nAre you sure you want to update it?`;
          const proceed = window.confirm(msg);
          if (!proceed) {
            closeEntityModal();
            return;
          }
        }
        const idx = arr.findIndex((e) => e.id === data.id);
        if (idx !== -1) {
          arr[idx] = { ...arr[idx], ...data };
        }
      } else if (entityModalState.value.mode === 'add') {
        // Assign a new id if not present
        if (!data.id) {
          data.id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        }
        arr.push({ ...data });
      }
    }
    await updateMovie(parentMovie.id, parentMovie);
  } else if (entityModalState.value.onSubmit) {
    entityModalState.value.onSubmit(data);
  }
  closeEntityModal();
}
</script>
<template>
  <div class="movie">
    <div class="movie-form-tree">
      <AccordionItem v-for="type in Object.keys(schema).filter((k) => k !== 'movie')" :id="getSectionId(type)" :key="type" :title="type">
        <RelationSection
          :relation="{ name: type, type: type, many: true }"
          :parent="movie"
          :parent-movie="movie"
          :schema="schema"
          :open-entity-modal="openEntityModal"
          :on-delete-entity="() => {}"
          @update="handleMovieUpdate"
        />
        <template #header-actions>
          <button class="icon" @click="openEntityModal('add', type, null, movie, movie, type)">library_add</button>
        </template>
      </AccordionItem>
    </div>
    <AppSidebar v-if="movie" :movie="movie" :schema="schema" />
    <EntityModal
      v-if="entityModalState && entityModalState.open"
      :is-open="entityModalState.open"
      :mode="entityModalState.mode"
      :type="entityModalState.type"
      :entity="entityModalState.entity"
      :parent="entityModalState.parent"
      :parent-movie="entityModalState.parentMovie"
      :relation-name="entityModalState.relationName"
      :on-submit="entityModalState.onSubmit"
      :schema="schema"
      @submit="handleEntityModalSubmit"
      @close="closeEntityModal"
    />
  </div>
</template>
